import React, { Fragment } from 'react';

import t from '../../../labels/labelling-utils';
import {
  DRIVERS_RANKING_FREE_PRACTICE_RESULTS,
  DRIVERS_RANKING_POINTS,
  DRIVERS_RANKING_QUALIFYING_RESULTS,
  DRIVERS_RANKING_RACE_RESULTS,
  DRIVERS_RANKING_RACE_STARTING_GRID,
  DriversRankingVariants,
} from '../../../typings/drivers-ranking';
import { IComponent } from '../../../typings/generic-component';
import { CtaTarget } from '../../../typings/match-row';
import { ITiming } from '../../../typings/motorsports';
import { RaceSessionStatus } from '../../../typings/race-session';
import { DEFAULT_STATE, NO_FASTEST_LAP_STATE, TableStates } from '../../../typings/table';
import { DRIVERFLAG, placeholdersMap } from '../../../utils/placeholders-motorsports';
import { formatTiming } from '../../../utils/times';
import Cta from '../../atoms/cta/cta';
import IconChronometer from '../../atoms/icons/icon-chronometer';
import ImgPlaceholder from '../../atoms/img-placeholder/img-placeholder';
import Table, { IColumn, IRow } from '../../molecules/table/table';

import './top-drivers-ranking-table.scss';

export interface ITopDriversRow extends IRow {
  bestLapTimeElement?: JSX.Element;
  driverLink?: JSX.Element;
  driverName: string;
  driverUrl?: string | null;
  fastestLap?: string; // WAS: bestTime
  gap: string | '-'; // WAS: time
  isFastestLap?: boolean; // FIXME: REAL field name still to be defined from IT PROG
  logoImage?: JSX.Element;
  logoUrl?: string | null;
  points?: string | null | '-';
  position: string;
  teamLink?: JSX.Element;
  teamName?: string;
  teamUrl?: string | null;
  timing?: ITiming; // WAS: totalRaceTime
}

interface ITopDriversTrackingData {
  dataAttribute?: string;
  driverLink?: string;
  teamLink?: string;
}

export interface ITopDriversTable extends IComponent {
  rows: ITopDriversRow[];
  className?: string;
  gpName?: string | null; // FIXME: to validate src/utils/dummy-data/motor-results-page.ts dummy data
  gpPageUrl?: string | null; // FIXME: to validate src/utils/dummy-data/motor-results-page.ts dummy data
  showTitles?: boolean;
  showRowSelected?: boolean;
  rankingVariant?: DriversRankingVariants;
  tableTitle?: string;
  status?: RaceSessionStatus;
  startDateTime?: string;
  showTimings?: boolean;
  trackingData?: ITopDriversTrackingData;
  target?: CtaTarget;
  hasLgpAnalytics?: boolean;
}

const CLASSNAME = 'top-drivers';
const TEAM_QUALIFYING_VARIANT = '-qualifying';
const TEAM_RACE_VARIANT = '-race';

const getTeamClassNameVariantsMap = () => {
  return {
    [DRIVERS_RANKING_POINTS]: '',
    [DRIVERS_RANKING_FREE_PRACTICE_RESULTS]: TEAM_QUALIFYING_VARIANT,
    [DRIVERS_RANKING_QUALIFYING_RESULTS]: TEAM_QUALIFYING_VARIANT,
    [DRIVERS_RANKING_RACE_STARTING_GRID]: TEAM_QUALIFYING_VARIANT,
    [DRIVERS_RANKING_RACE_RESULTS]: TEAM_RACE_VARIANT,
  };
};

const GAP_DEFAULT_PREFIX = '+';

const getGapLabel = ({ gap, position }: ITopDriversRow): string =>
  gap === '-' || gap === '' ? gap : `${position === '-' ? '' : GAP_DEFAULT_PREFIX}${gap}`;

const getTimeData = (item: ITopDriversRow, index: number, status?: RaceSessionStatus): JSX.Element =>
  index === 0
    ? getBestLapTimeElement(formatTiming(item.timing), Boolean(item.isFastestLap))
    : getBestLapTimeElement(`${getGapLabel(item)}`, Boolean(item.isFastestLap));

const getNoTimingsElement = (): JSX.Element => <Fragment>{'-'}</Fragment>;

// TODO: Shoud I Use <BestLap> component?
const getBestLapTimeElement = (value?: string, isFastestLap?: boolean): JSX.Element =>
  value ? (
    <Fragment>
      <div style={{ display: 'inline-flex' }}>
        {isFastestLap && <IconChronometer size="xxsmall" />}
        {value}
      </div>
    </Fragment>
  ) : (
    <Fragment>{value}</Fragment>
  );

/**
 * Get the tracking data for full clickable row
 * @param trackingData tracking element
 * @param showRowSelected shoud show full row selected
 * @returns tracking data object
 */
const getTrackingDataRow = (trackingData: ITopDriversTrackingData | undefined): object =>
  trackingData && trackingData.dataAttribute
    ? {
        [trackingData.dataAttribute]: trackingData.driverLink,
      }
    : {};

/**
 * Get Team Element depending on item ITopDriversRow
 * @param item ITopDriversRow element
 * @param trackingData tracking element
 * @returns JSX element
 */
const getTeamLink = (
  item: ITopDriversRow,
  trackingData: ITopDriversTrackingData | undefined,
  target?: string
): JSX.Element => {
  if (!item.teamUrl) {
    return <Fragment>{item.teamName}</Fragment>;
  } else if (trackingData && trackingData.dataAttribute) {
    const trackingObj = {
      [trackingData.dataAttribute]: trackingData.teamLink,
    };
    return (
      <Cta
        href={item.teamUrl}
        uppercase={false}
        narrow
        trackingData={trackingObj}
        target={target || undefined}
        hasWrapper={false}
      >
        {item.teamName}
      </Cta>
    );
  }
  return (
    <Cta href={item.teamUrl} uppercase={false} narrow target={target || undefined} hasWrapper={false}>
      {item.teamName}
    </Cta>
  );
};

/**
 * Get Driver JSX element depending on item ITopDriversRow
 * @param item ITopDriversRow element
 * @param trackingData tracking element
 * @returns JSX element
 */
const getDriverLink = (
  item: ITopDriversRow,
  trackingData: ITopDriversTrackingData | undefined,
  target?: string
): JSX.Element => {
  if (!item.driverUrl) {
    return <Fragment>{item.driverName}</Fragment>;
  } else if (trackingData && trackingData.dataAttribute) {
    const trackingObj = {
      [trackingData.dataAttribute]: trackingData.driverLink,
    };
    return (
      <Cta
        href={item.driverUrl}
        trackingData={trackingObj}
        uppercase={false}
        narrow
        target={target || undefined}
        hasWrapper={false}
      >
        {item.driverName}
      </Cta>
    );
  }
  return (
    <Cta href={item.driverUrl} uppercase={false} narrow target={target || undefined} hasWrapper={false}>
      {item.driverName}
    </Cta>
  );
};

const TopDriversTable = ({
  /* istanbul ignore next: default value */ rows = [],
  /* istanbul ignore next: default value */ className = CLASSNAME,
  /* istanbul ignore next: default value */ showTitles = true,
  /* istanbul ignore next: default value */ showRowSelected = false,
  /* istanbul ignore next: default value */ rankingVariant = DRIVERS_RANKING_POINTS,
  showTimings = true,
  status,
  trackingData,
  target,
  hasLgpAnalytics,
}: ITopDriversTable) => {
  const teamClassNameVariantsMap = getTeamClassNameVariantsMap();
  const columns: Array<IColumn> = [
    {
      title: `${t('top.drivers.th.position')}`,
      data: 'position',
      classModifier: 'position',
    },
    {
      title: `${t('top.drivers.th.flag')}`, // ISS-4676 - SEO do not allow for EMPTY TH
      data: 'logoImage',
      colspan: 1,
      classModifier: 'logo',
    },
    {
      title: `${t('top.drivers.th.name')}`,
      data: 'driverLink',
      colspan: 2,
      classModifier: 'name',
    },
    {
      title: `${t('top.drivers.th.team')}`,
      data: 'teamLink',
      classModifier: `team${teamClassNameVariantsMap[rankingVariant]}`,
    },
  ];

  const hasFastestLapColumn: boolean = rows.some((row: ITopDriversRow) => row.fastestLap && row.fastestLap !== '-');
  const tableState: TableStates = !hasFastestLapColumn ? NO_FASTEST_LAP_STATE : DEFAULT_STATE;
  const timeColumns: IColumn[] = hasFastestLapColumn
    ? [
        {
          title: `${t('top.drivers.th.best.time')}`,
          data: 'bestLapTimeElement',
          classModifier: 'best-time',
        },
        {
          title: `${t('top.drivers.th.time')}`,
          data: 'time', // WAS: 'totalRaceTime',
          classModifier: 'total-race-time',
        },
      ]
    : [
        {
          title: `${t('top.drivers.th.time')}`,
          data: 'time', // WAS: 'totalRaceTime',
          classModifier: 'total-race-time',
        },
      ];

  /** define ranking variant */
  switch (rankingVariant) {
    case DRIVERS_RANKING_POINTS:
      columns.push({
        title: `${t('top.drivers.th.points')}`,
        data: 'points',
        classModifier: 'points',
      });
      break;
    case DRIVERS_RANKING_FREE_PRACTICE_RESULTS:
    case DRIVERS_RANKING_QUALIFYING_RESULTS:
    case DRIVERS_RANKING_RACE_STARTING_GRID:
      columns.push({
        title: `${t('top.drivers.th.time')}`,
        data: 'time',
        classModifier: 'times',
      });
      break;
    case DRIVERS_RANKING_RACE_RESULTS:
      columns.push(...timeColumns);
      break;
    /* istanbul ignore next: unreachable code */
    default:
  }

  const _rows: ITopDriversRow[] = rows.map((item: ITopDriversRow, index: number) => ({
    ...item,
    classRow: (_row: ITopDriversRow) => showRowSelected && (_row.driverUrl || _row.teamUrl),
    bestLapTimeElement: showTimings ? getBestLapTimeElement(item.fastestLap, item.isFastestLap) : getNoTimingsElement(),
    driverLink: !showRowSelected ? getDriverLink(item, trackingData, target) : <Fragment>{item.driverName}</Fragment>,
    logoImage: (
      <ImgPlaceholder src={item.logoUrl || ''} loading="lazy" aria-hidden placeholder={placeholdersMap[DRIVERFLAG]} />
    ),
    rowLink: showRowSelected ? item.driverUrl || item.teamUrl || undefined : undefined,
    teamLink: !showRowSelected ? getTeamLink(item, trackingData, target) : <Fragment>{item.teamName}</Fragment>,
    time: showTimings ? getTimeData(item, index, status) : '-',
    trackingData: showRowSelected ? getTrackingDataRow(trackingData) : {},
  }));

  return (
    <Table
      columns={columns}
      rows={_rows}
      className={className}
      showTitles={showTitles}
      state={tableState}
      hasLgpAnalytics={hasLgpAnalytics}
    />
  );
};

export default TopDriversTable;
